import { useToast } from '@repo/design-system/index'
import {
  MutationFunction,
  UseMutationOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import axios, { AxiosError, AxiosResponse } from 'axios'
import { toast } from 'react-toastify'
import {
  AccuracyMetricsDetailItem,
  AnalysisCompDetailResponse,
  AnalysisComponentDetailList,
  CAFOverviewResponse,
  CafShowTooltipResponse,
  CafSummaryOverview,
  ComplexityDetailItem,
  HighlightDetailResponse,
  HighlightTagGraphDataType,
  MetricAverageResponse,
  ScoresResponse,
  SummaryDatesResponse,
  SummaryHighlightResponse,
  SummaryHighlightTagResponse,
  SummaryLessonsResponse,
  showJobBannerResponse,
} from '../../../model/caf.interface'
import apis, { webview_apis } from '../../apis'
import { getCafToken, getJwtToken } from '../../auth'
import { axiosGet } from '../../axiosHelper'
import useIsWebview from '../../hooks/common/useIsWebview'
import { s_caf } from '../../i18n/strings/auto/wording_webview'
import useTranslation from '../../i18n/useTranslation'
import {
  cafAnalysisAverageScoresQueryApi,
  cafAnalysisAverageScoresQueryApiV5,
  cafAnalysisComponentDetailListQueryApi,
  cafAnalysisComponentDetailListQueryApiV5,
  cafAnalysisComponentListDetailQueryApi,
  cafAnalysisComponentListDetailQueryApiV5,
  cafAnalysisComponentListQueryApi,
  cafAnalysisComponentListQueryApiV5,
  cafRequestAnalysisComponentListQueryApiV5,
  cafScoresQueryApi,
  cafScoresQueryApiV5,
  cafSummaryDatesQueryApi,
  cafSummaryDatesQueryApiV5,
  cafSummaryHighlightsQueryApi,
  cafSummaryHighlightsQueryApiV5,
  cafSummaryOverviewQueryApi,
  cafSummaryOverviewQueryApiV5,
} from '../../react-query/queryApis'
import { createAxiosTokenConfig } from '../axios-util'

/**
 * CAF QUERIES
 */
export const getHighlightData = async (highlightId: number, page = 1, size = 5) => {
  const token = getJwtToken()
  const { data } = await axios.get(
    webview_apis.caf.highlight.detail(highlightId, page, size),
    createAxiosTokenConfig(token)
  )
  console.log(webview_apis.caf.highlight.detail(highlightId, page, size), 'HIGHLIGHT DATA::::', data)
  return data
}

export const useCafHighlight = (highlightId: number, page = 1, size = 5) => {
  return useInfiniteQuery<HighlightDetailResponse, Error, HighlightDetailResponse, any>({
    queryKey: [webview_apis.caf.highlight.detail(highlightId, page, size), highlightId],
    queryFn: async ({ pageParam = 1 }) => getHighlightData(highlightId, pageParam, size),
    getNextPageParam: (data) => {
      const {
        page: { next },
      } = data
      return next
    },
    retry: false,
    enabled: highlightId >= 0,
  })
}

interface Response<T> {
  config: any
  data: T
  status: number
}
export const useCafAIParaphrase = (
  options: Omit<UseMutationOptions<AxiosResponse<any, any>, unknown, number, unknown>, 'mutationFn'> = {}
) => {
  const token = getJwtToken()
  return useMutation({
    mutationFn: async (highlightId) =>
      await axios.post(webview_apis.caf.highlight.ai.paraphrase(highlightId), null, {
        ...createAxiosTokenConfig(token),
      }),
    ...options,
  })
}

export const useCafAIFeedback = <T>(
  options: Omit<UseMutationOptions<AxiosResponse<T>, unknown, [number, boolean], unknown>, 'mutationFn'> = {}
) => {
  const token = getJwtToken()
  const mutationFn: MutationFunction<AxiosResponse<T>, [number, boolean]> = async (variables) => {
    const [highlightId, up] = variables
    return await axios.post<T>(
      webview_apis.caf.highlight.ai.feedback(highlightId),
      { up: up },
      {
        ...createAxiosTokenConfig(token),
      }
    )
  }
  return useMutation<AxiosResponse<T>, unknown, [number, boolean], unknown>({ mutationFn, ...options })
}
export const useCafHighlightList = <T>({ lessonId, pinned = false, recommended = false, show_details = false }) => {
  const cafToken = getCafToken()
  const jwtToken = getJwtToken()
  const token = cafToken || jwtToken
  return useQuery<T>({
    queryKey: [webview_apis.caf.highlight.list(lessonId), { pinned, recommended, show_details }, 'highlight'],
    queryFn: async () => {
      const { data } = await axios.get(webview_apis.caf.highlight.list(lessonId), {
        ...createAxiosTokenConfig(token),
        params: { pinned, recommended, show_details },
      })
      console.log('QUERY RESPONSE :: ', data)
      return data
    },
    enabled: !!lessonId,
    retry: false,
  })
}

export const useCafPinned = () => {
  const token = getJwtToken()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: async (body: { tag: number; active: boolean }) => {
      await axios.put(
        webview_apis.caf.highlight.pin({ tag: body.tag, active: body.active }),
        null,
        createAxiosTokenConfig(token)
      )
    },
    // onError: (error) => {
    //   console.log('error', error)
    //   toast(t(s_caf.highlight_list_edit.toast3))
    // },
  })
}

export const useHighlightErrorReport = () => {
  const token = getJwtToken()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: async (body: { id: number; content: string }) => {
      await axios.post(
        webview_apis.caf.report.highlight(body.id),
        { content: body.content },
        createAxiosTokenConfig(token)
      )
    },
    onSuccess: () => {
      toast(t(s_caf.accuracy.error_toast))
    },
  })
}

export const useMetricErrorReport = () => {
  const token = getJwtToken()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: async (body: { id: number; content: string }) => {
      await axios.post(
        webview_apis.caf.report.metric(body.id),
        { content: body.content },
        createAxiosTokenConfig(token)
      )
    },
    onSuccess: () => {
      toast(t(s_caf.accuracy.error_toast))
    },
  })
}

export const useCafAnalysisComponentList = (lessonId: number, enabled: boolean) => {
  const pathParams = { lessonId }

  return useQuery<CAFOverviewResponse, AxiosError>({
    queryKey: cafAnalysisComponentListQueryApi.getQueryKey({ pathParams }),
    queryFn: cafAnalysisComponentListQueryApi.getQueryFn({ pathParams }),

    // onError: (e) => handleAxiosError(e),
    enabled: !!lessonId && enabled,
    staleTime: 60000,
    retry: false,
  })
}

export const useCafAnalysisComponentDetail = (componentId: number) => {
  const pathParams = { componentId }

  return useQuery<AnalysisCompDetailResponse, AxiosError>({
    queryKey: cafAnalysisComponentListDetailQueryApi.getQueryKey({ pathParams }),
    queryFn: cafAnalysisComponentListDetailQueryApi.getQueryFn({ pathParams }),

    // onError: (e) => handleAxiosError(e),
    enabled: !!componentId,
    staleTime: 60000,
    retry: false,
  })
}

export const useCafAnalysisComponentDetailList = (componentId: number) => {
  const pathParams = { componentId }

  return useQuery<AnalysisComponentDetailList, AxiosError>({
    queryKey: cafAnalysisComponentDetailListQueryApi.getQueryKey({ pathParams }),
    queryFn: cafAnalysisComponentDetailListQueryApi.getQueryFn({ pathParams }),

    // onError: (e) => handleAxiosError(e),
    enabled: !!componentId,
    staleTime: 60000,
    retry: false,
  })
}

type CafMetricDetailsType = AccuracyMetricsDetailItem | ComplexityDetailItem
export const useCafMetricsDetails = <T extends CafMetricDetailsType>(
  metricId: number,
  tag: number,
  size = 10,
  page = 1
) => {
  return useInfiniteQuery<T, Error, T, any>({
    queryKey: [webview_apis.caf.metric.detail(metricId, tag, page, size)],
    queryFn: async ({ pageParam = 1 }) =>
      await axiosGet(webview_apis.caf.metric.detail(metricId, tag, pageParam, size)),
    getNextPageParam: (response) => {
      return response?.page.next
    },
    enabled: !!metricId,
  })
}

/**
 * CAF 2차 베타 추가 Routes
 */

export const useCafSummaryOverview = ({ startDate, endDate }: { startDate: string; endDate: string }) => {
  const searchParams = { start_date: startDate, end_date: endDate }

  return useQuery<CafSummaryOverview, AxiosError>({
    queryKey: cafSummaryOverviewQueryApi.getQueryKey({ searchParams }),
    queryFn: cafSummaryOverviewQueryApi.getQueryFn({ searchParams }),
    // onError: (e) => handleAxiosError(e),
    retry: false,
    initialData: {
      count: {
        twenty: 1,
        forty: 0,
      },
      average: {
        overall: 0,
        complexity: 0,
        accuracy: 0,
        fluency: 0,
      },
      trend: {
        overall: [],
        complexity: [],
        accuracy: [],
        fluency: [],
      },
      show_summary_popup: false,
      summary_popup_type: 0,
    },
  })
}
export const useCafSummaryOverviewOnlyOnRefetch = ({
  startDate,
  endDate,
  enabled,
}: {
  startDate: string
  endDate: string
  enabled: boolean
}) => {
  const searchParams = { start_date: startDate, end_date: endDate }

  return useQuery<CafSummaryOverview, AxiosError>({
    queryKey: cafSummaryOverviewQueryApi.getQueryKey({ searchParams }),
    queryFn: cafSummaryOverviewQueryApi.getQueryFn({ searchParams }),
    // onError: (e) => handleAxiosError(e),
    retry: true,
    enabled: enabled,
  })
}

export type CafSummaryHighlightsParams = {
  startDate?: string
  endDate?: string
  pinned: boolean
  recommended: boolean
}

export const useCafSummaryHighlights = ({ startDate, endDate, pinned, recommended }: CafSummaryHighlightsParams) => {
  const searchParams = { start_date: startDate, end_date: endDate, pinned, recommended }

  return useQuery<SummaryHighlightResponse, AxiosError>({
    queryKey: cafSummaryHighlightsQueryApi.getQueryKey({ searchParams }),
    queryFn: cafSummaryHighlightsQueryApi.getQueryFn({ searchParams }),
    retry: false,
    // onError: (err) => console.log(err),
    staleTime: 500,
  })
}

export const useCafSummaryHighlightTag = (tag: number, { startDate, endDate, size = 10 }) => {
  return useInfiniteQuery<SummaryHighlightTagResponse, Error, SummaryHighlightTagResponse, any>({
    queryKey: [webview_apis.caf.summary.highlights.tag(tag, { startDate, endDate, pageParam: 1 }, size)],
    queryFn: async ({ pageParam = 1 }) =>
      await axiosGet(webview_apis.caf.summary.highlights.tag(tag, { startDate, endDate, pageParam }, size)),
    getNextPageParam: ({ next, highlights }) => {
      return next
    },
    enabled: tag >= 0,
  })
}
export const useCafHighlightTagGraphData = (tag: number, { startDate, endDate, size = 9999 }) => {
  return useQuery<HighlightTagGraphDataType, Error, HighlightTagGraphDataType, any>({
    queryKey: [webview_apis.caf.summary.highlights.tag(tag, { startDate, endDate, pageParam: 1 }, size)],
    queryFn: async () =>
      await axiosGet(webview_apis.caf.summary.highlights.tag(tag, { startDate, endDate, pageParam: 1 }, size)),
    enabled: tag >= 0,
  })
}

export const useCafSummaryLessons = (startDate, endDate) => {
  return useInfiniteQuery<SummaryLessonsResponse, Error, SummaryLessonsResponse, any>({
    queryKey: [webview_apis.caf.summary.lessons(startDate, endDate)],
    queryFn: async ({ pageParam = 1 }) =>
      await axiosGet(webview_apis.caf.summary.lessons(startDate, endDate, pageParam)),
    getNextPageParam: ({ next, lessons }) => {
      return next
    },
    staleTime: 60000,
    retry: false,
  })
}

export const useCafRecentLessonId = () => {
  return useQuery<SummaryLessonsResponse, Error, number, any>({
    queryKey: webview_apis.caf.summary.lessons(null, null, 1, 1),
    queryFn: async () => await axiosGet(webview_apis.caf.summary.lessons(null, null, 1, 1)),
    staleTime: 60000,
    retry: false,
    select: (data: SummaryLessonsResponse) => (data.count ? data.lessons[0].lesson_id : undefined),
  })
}

export const useCafOverviewChecked = () => {
  const token = getJwtToken()
  return useMutation({
    mutationFn: async (lessonId: number) =>
      await axios.post(webview_apis.caf.popup.overview.check({ lessonId }), null, createAxiosTokenConfig(token)),
  })
}
export const useCafOverviewCancelPopup = (lessonId: number) => {
  const token = getJwtToken()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () =>
      await axios.post(webview_apis.caf.popup.overview.cancel({ lessonId }), null, createAxiosTokenConfig(token)),
    onSuccess: () => {
      queryClient.invalidateQueries(
        cafAnalysisComponentListQueryApi.getQueryKey({ pathParams: { lessonId: lessonId } })
      )
    },
  })
}
export const useCafSummaryCancelPopup = () => {
  const token = getJwtToken()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () =>
      await axios.post(webview_apis.caf.popup.summary.cancel(), null, createAxiosTokenConfig(token)),
    onSuccess: () => {
      queryClient.invalidateQueries([webview_apis.caf.summary.overview()])
    },
  })
}

export const useCafSummaryLessonSetting = (callback: () => void) => {
  const token = getJwtToken()
  return useMutation({
    mutationFn: async (body: { id: number; status: boolean }) =>
      await axios.patch(webview_apis.caf.summary.setting(body.id, body.status), null, createAxiosTokenConfig(token)),
    onSuccess: () => {
      callback()
    },
  })
}

export const useCafAnalysisAverageScores = (type: any) => {
  const searchParams = { type }

  return useQuery<MetricAverageResponse<typeof type>, AxiosError>({
    queryKey: cafAnalysisAverageScoresQueryApi.getQueryKey({ searchParams }),
    queryFn: cafAnalysisAverageScoresQueryApi.getQueryFn({ searchParams }),

    // onError: (e) => handleAxiosError(e),
    staleTime: 60000,
    retry: false,
  })
}

/**
 * CAF 3차 베타 추가 Routes
 */

export const useCafSummaryDates = ({ year, month }: { year: number; month: number }) => {
  const searchParams = { year, month }

  return useQuery<SummaryDatesResponse, AxiosError>({
    queryKey: cafSummaryDatesQueryApi.getQueryKey({ searchParams }),
    queryFn: cafSummaryDatesQueryApi.getQueryFn({ searchParams }),

    // onError: (e) => handleAxiosError(e),
    staleTime: 60000,
    retry: false,
  })
}

export const useCafScores = (type: string) => {
  const searchParams = { type }

  return useQuery<ScoresResponse, AxiosError>({
    queryKey: cafScoresQueryApi.getQueryKey({ searchParams }),
    queryFn: cafScoresQueryApi.getQueryFn({ searchParams }),

    // onError: (e) => handleAxiosError(e),
    staleTime: 60000,
    retry: false,
  })
}

/**
 *
 *  3.0 CAF 아래
 *
 */

/**
 * CAF QUERIES
 */
export const getHighlightDataV5 = async (highlightId: number, page = 1, size = 5) => {
  const token = getJwtToken()
  const { data } = await axios.get(apis.caf.highlight.detail(highlightId, page, size), createAxiosTokenConfig(token))
  return data
}

export const useCafHighlightV5 = (highlightId: number, page = 1, size = 5) => {
  return useInfiniteQuery<HighlightDetailResponse, Error, HighlightDetailResponse, any>({
    queryKey: [apis.caf.highlight.detail(highlightId, page, size), highlightId],
    queryFn: async ({ pageParam = 1 }) => getHighlightDataV5(highlightId, pageParam, size),
    getNextPageParam: (data) => {
      const {
        page: { next },
      } = data
      return next
    },
    retry: false,
    enabled: highlightId >= 0,
  })
}

interface Response<T> {
  config: any
  data: T
  status: number
}
export const useCafAIParaphraseV5 = (
  options: Omit<UseMutationOptions<AxiosResponse<any, any>, unknown, number, unknown>, 'mutationFn'> = {}
) => {
  const token = getJwtToken()
  return useMutation({
    mutationFn: async (highlightId) =>
      await axios.post(apis.caf.highlight.ai.paraphrase(highlightId), null, {
        ...createAxiosTokenConfig(token),
      }),
    ...options,
  })
}

export const useCafAIFeedbackV5 = <T>(
  options: Omit<UseMutationOptions<AxiosResponse<T>, unknown, [number, boolean], unknown>, 'mutationFn'> = {}
) => {
  const token = getJwtToken()
  const mutationFn: MutationFunction<AxiosResponse<T>, [number, boolean]> = async (variables) => {
    const [highlightId, up] = variables
    return await axios.post<T>(
      apis.caf.highlight.ai.feedback(highlightId),
      { up: up },
      {
        ...createAxiosTokenConfig(token),
      }
    )
  }
  return useMutation<AxiosResponse<T>, unknown, [number, boolean], unknown>({ mutationFn, ...options })
}
export const useCafHighlightListV5 = <T>({ lessonId, pinned = false, recommended = false, show_details = false }) => {
  const token = getJwtToken()
  return useQuery<T>({
    queryKey: [apis.caf.highlight.list(lessonId), { pinned, recommended, show_details }, 'highlight'],
    queryFn: async () => {
      const { data } = await axios.get(apis.caf.highlight.list(lessonId), {
        ...createAxiosTokenConfig(token),
        params: { pinned, recommended, show_details },
      })
      console.log('QUERY RESPONSE :: ', data)
      return data
    },
    enabled: !!lessonId,
    retry: false,
    // onError: (err) => {
    //   console.log({ err })
    // },
  })
}

export const useCafPinnedV5 = () => {
  const token = getJwtToken()
  const { t } = useTranslation()
  const isWebview = useIsWebview()
  const { toastSuccess } = useToast()
  return useMutation({
    mutationFn: async (body: { tag: number; active: boolean }) => {
      await axios.put(
        apis.caf.highlight.pin({ tag: body.tag, active: body.active }),
        null,
        createAxiosTokenConfig(token)
      )
    },
    // onError: (error) => {
    //   isWebview ? toastSuccess(t(n_caf.highlight_list_edit.toast3)) : toast(t(n_caf.highlight_list_edit.toast3))
    // },
  })
}

export const useHighlightErrorReportV5 = () => {
  const token = getJwtToken()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: async (body: { id: number; content: string }) => {
      await axios.post(apis.caf.report.highlight(body.id), { content: body.content }, createAxiosTokenConfig(token))
    },
    // onError: (error) => {
    //   toast('Try again.')
    // },
    onSuccess: () => {
      toast(t(s_caf.accuracy.error_toast))
    },
  })
}

export const useMetricErrorReportV5 = () => {
  const token = getJwtToken()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: async (body: { id: number; content: string }) => {
      await axios.post(apis.caf.report.metric(body.id), { content: body.content }, createAxiosTokenConfig(token))
    },
    // onError: (error) => {
    //   toast('Try again.')
    // },
    onSuccess: () => {
      toast(t(s_caf.accuracy.error_toast))
    },
  })
}

export const useCafAnalysisComponentListV5 = (lessonId: number, enabled: boolean) => {
  const pathParams = { lessonId }

  return useQuery<CAFOverviewResponse, AxiosError>({
    queryKey: cafAnalysisComponentListQueryApiV5.getQueryKey({ pathParams }),
    queryFn: cafAnalysisComponentListQueryApiV5.getQueryFn({ pathParams }),

    // onError: (e) => handleAxiosError(e),
    enabled: !!lessonId && enabled,
    staleTime: 60000,
    retry: false,
  })
}

export const useCafAnalysisComponentDetailV5 = (componentId: number) => {
  const pathParams = { componentId }

  return useQuery<AnalysisCompDetailResponse, AxiosError>({
    queryKey: cafAnalysisComponentListDetailQueryApiV5.getQueryKey({ pathParams }),
    queryFn: cafAnalysisComponentListDetailQueryApiV5.getQueryFn({ pathParams }),

    // onError: (e) => handleAxiosError(e),
    enabled: !!componentId,
    staleTime: 60000,
    retry: false,
  })
}

export const useCafAnalysisComponentDetailListV5 = (componentId: number) => {
  const pathParams = { componentId }

  return useQuery<AnalysisComponentDetailList, AxiosError>({
    queryKey: cafAnalysisComponentDetailListQueryApiV5.getQueryKey({ pathParams }),
    queryFn: cafAnalysisComponentDetailListQueryApiV5.getQueryFn({ pathParams }),

    // onError: (e) => handleAxiosError(e),
    enabled: !!componentId,
    staleTime: 60000,
    retry: false,
  })
}

type CafMetricDetailsTypeV5 = AccuracyMetricsDetailItem | ComplexityDetailItem
export const useCafMetricsDetailsV5 = <T extends CafMetricDetailsTypeV5>(
  metricId: number,
  tag: number,
  size = 10,
  page = 1
) => {
  return useInfiniteQuery<T, Error, T, any>({
    queryKey: [apis.caf.metric.detail(metricId, tag, page, size)],
    queryFn: async ({ pageParam = 1 }) => await axiosGet(apis.caf.metric.detail(metricId, tag, pageParam, size)),
    getNextPageParam: (response) => {
      return response?.page.next
    },
    enabled: !!metricId,
  })
}

/**
 * CAF 2차 베타 추가 Routes
 */

export const useCafSummaryOverviewV5 = ({
  startDate,
  endDate,
  isMyRingle = false,
}: {
  startDate: string
  endDate: string
  isMyRingle?: boolean
}) => {
  const searchParams = { start_date: startDate, end_date: endDate, is_my_ringle: isMyRingle }

  return useQuery<CafSummaryOverview, AxiosError>({
    queryKey: cafSummaryOverviewQueryApiV5.getQueryKey({ searchParams }),
    queryFn: cafSummaryOverviewQueryApiV5.getQueryFn({ searchParams }),
    // onError: (e) => handleAxiosError(e),
    retry: false,
    initialData: {
      count: {
        twenty: 0,
        forty: 0,
      },
      average: {
        overall: 0,
        complexity: 0,
        accuracy: 0,
        fluency: 0,
      },
      trend: {
        overall: [],
        complexity: [],
        accuracy: [],
        fluency: [],
        pronunciation: [],
      },
      show_summary_popup: false,
      summary_popup_type: 0,
    },
  })
}
export const useCafSummaryOverviewOnlyOnRefetchV5 = ({
  startDate,
  endDate,
  enabled,
}: {
  startDate: string
  endDate: string
  enabled: boolean
}) => {
  const searchParams = { start_date: startDate, end_date: endDate }

  return useQuery<CafSummaryOverview, AxiosError>({
    queryKey: cafSummaryOverviewQueryApiV5.getQueryKey({ searchParams }),
    queryFn: cafSummaryOverviewQueryApiV5.getQueryFn({ searchParams }),
    // onError: (e) => handleAxiosError(e),
    retry: true,
    enabled: enabled,
  })
}

export type CafSummaryHighlightsParamsV5 = {
  startDate?: string
  endDate?: string
  pinned: boolean
  recommended: boolean
}

export const useCafSummaryHighlightsV5 = ({
  startDate,
  endDate,
  pinned,
  recommended,
}: CafSummaryHighlightsParamsV5) => {
  const searchParams = { start_date: startDate, end_date: endDate, pinned, recommended }

  return useQuery<SummaryHighlightResponse, AxiosError>({
    queryKey: cafSummaryHighlightsQueryApiV5.getQueryKey({ searchParams }),
    queryFn: cafSummaryHighlightsQueryApiV5.getQueryFn({ searchParams }),
    retry: false,
    // onError: (err) => console.log(err),
    staleTime: 500,
  })
}

export const useCafSummaryHighlightTagV5 = (tag: number, { startDate, endDate, size = 10 }) => {
  return useInfiniteQuery<SummaryHighlightTagResponse, Error, SummaryHighlightTagResponse, any>({
    queryKey: [apis.caf.summary.highlights.tag(tag, { startDate, endDate, pageParam: 1 }, size)],
    queryFn: async ({ pageParam = 1 }) =>
      await axiosGet(apis.caf.summary.highlights.tag(tag, { startDate, endDate, pageParam }, size)),
    getNextPageParam: ({ next, highlights }) => {
      return next
    },
    enabled: tag >= 0,
  })
}
export const useCafHighlightTagGraphDataV5 = (tag: number, { startDate, endDate, size = 9999 }) => {
  return useQuery<HighlightTagGraphDataType, Error, HighlightTagGraphDataType, any>({
    queryKey: [apis.caf.summary.highlights.tag(tag, { startDate, endDate, pageParam: 1 }, size)],
    queryFn: async () =>
      await axiosGet(apis.caf.summary.highlights.tag(tag, { startDate, endDate, pageParam: 1 }, size)),
    enabled: tag >= 0,
  })
}

export const useCafSummaryLessonsV5 = (startDate, endDate) => {
  return useInfiniteQuery<SummaryLessonsResponse, Error, SummaryLessonsResponse, any>({
    queryKey: [apis.caf.summary.lessons(startDate, endDate)],
    queryFn: async ({ pageParam = 1 }) => await axiosGet(apis.caf.summary.lessons(startDate, endDate, pageParam)),
    getNextPageParam: ({ next, lessons }) => {
      return next
    },
    staleTime: 60000,
    retry: false,
  })
}

export const useCafRecentLessonIdV5 = () => {
  return useQuery<SummaryLessonsResponse, Error, number, any>({
    queryKey: apis.caf.summary.lessons(null, null, 1, 1),
    queryFn: async () => await axiosGet(apis.caf.summary.lessons(null, null, 1, 1)),
    staleTime: 60000,
    retry: false,
    select: (data: SummaryLessonsResponse) => (data.count ? data.lessons[0].lesson_id : undefined),
  })
}

export const useCafOverviewCheckedV5 = () => {
  const token = getJwtToken()
  return useMutation({
    mutationFn: async (lessonId: number) =>
      await axios.post(apis.caf.popup.overview.check({ lessonId }), null, createAxiosTokenConfig(token)),
  })
}
export const useCafOverviewCancelPopupV5 = (lessonId: number) => {
  const token = getJwtToken()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () =>
      await axios.post(apis.caf.popup.overview.cancel({ lessonId }), null, createAxiosTokenConfig(token)),
    onSuccess: () => {
      queryClient.invalidateQueries(
        cafAnalysisComponentListQueryApi.getQueryKey({ pathParams: { lessonId: lessonId } })
      )
    },
  })
}
export const useCafSummaryCancelPopupV5 = () => {
  const token = getJwtToken()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => await axios.post(apis.caf.popup.summary.cancel(), null, createAxiosTokenConfig(token)),
    onSuccess: () => {
      queryClient.invalidateQueries([apis.caf.summary.overview()])
    },
  })
}

export const useCafSummaryLessonSettingV5 = (callback: () => void) => {
  const token = getJwtToken()
  return useMutation({
    mutationFn: async (body: { id: number; status: boolean }) =>
      await axios.patch(apis.caf.summary.setting(body.id, body.status), null, createAxiosTokenConfig(token)),
    onSuccess: () => {
      callback()
    },
  })
}

export const useCafAnalysisAverageScoresV5 = (type: any) => {
  const searchParams = { type }

  return useQuery<MetricAverageResponse<typeof type>, AxiosError>({
    queryKey: cafAnalysisAverageScoresQueryApiV5.getQueryKey({ searchParams }),
    queryFn: cafAnalysisAverageScoresQueryApiV5.getQueryFn({ searchParams }),

    // onError: (e) => handleAxiosError(e),
    staleTime: 60000,
    retry: false,
  })
}

/**
 * CAF 3차 베타 추가 Routes
 */

export const useCafSummaryDatesV5 = ({ year, month }: { year: number; month: number }) => {
  const searchParams = { year, month }

  return useQuery<SummaryDatesResponse, AxiosError>({
    queryKey: cafSummaryDatesQueryApiV5.getQueryKey({ searchParams }),
    queryFn: cafSummaryDatesQueryApiV5.getQueryFn({ searchParams }),

    // onError: (e) => handleAxiosError(e),
    staleTime: 60000,
    retry: false,
  })
}

export const useCafScoresV5 = (type: string) => {
  const searchParams = { type }

  return useQuery<ScoresResponse, AxiosError>({
    queryKey: cafScoresQueryApiV5.getQueryKey({ searchParams }),
    queryFn: cafScoresQueryApiV5.getQueryFn({ searchParams }),

    // onError: (e) => handleAxiosError(e),
    staleTime: 60000,
    retry: false,
  })
}

/* tool tip 사용 여부 판단 API (get, put) */
export const useCafShowTooltipV4 = (key: string) => {
  const token = getJwtToken()

  return useQuery<CafShowTooltipResponse, AxiosError>({
    queryKey: [webview_apis.caf.show_tooltip(key)],
    queryFn: async () => {
      const { data } = await axios.get(webview_apis.caf.show_tooltip(key), createAxiosTokenConfig(token))
      console.log('QUERY RESPONSE :: ', data)
      return data
    },
    retry: false,
    // onError: (err) => {
    //   console.log({ err })
    // },
  })
}

export const useCafEditTooltipV4 = () => {
  const token = getJwtToken()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: async (body: { key: string; value: boolean }) => {
      await axios.put(webview_apis.caf.edit_tooltip(body.key, body.value), null, createAxiosTokenConfig(token))
    },
    // onError: (error) => {
    //   console.log('error', error)
    //   toast(t(s_caf.highlight_list_edit.toast3))
    // },
  })
}

/* banner 표시 여부 판단 API (get, put) */
export const useShowJobBannerV4 = (key: string, pageType: string, lessonId?: number) => {
  const token = getJwtToken()

  const customizedKey = () => {
    if (key === 'job_analysis_banner') {
      if (pageType === 'review') {
        return `caf_${pageType}_${lessonId}_job_analysis_banner`
      } else if (pageType === 'statistic' || pageType === 'myRingle') {
        return `caf_${pageType}_job_analysis_banner`
      } else {
        console.log('===== error in useShowJobBannerV4') // TODO: 에러 발생시키기
        return
      }
    } else return key
  }

  return useQuery<showJobBannerResponse, AxiosError>({
    queryKey: [apis.caf.jobAnalysis.showBanner(customizedKey())],
    queryFn: async () => {
      const { data } = await axios.get(apis.caf.jobAnalysis.showBanner(customizedKey()), createAxiosTokenConfig(token))
      console.log('QUERY RESPONSE :: ', data)
      return data
    },
    retry: false,
    // onError: (err) => {
    //   console.log({ err })
    // },
  })
}

export const useEditJobBannerV4 = () => {
  const token = getJwtToken()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: async (body: { key: string; value: boolean }) => {
      await axios.put(apis.caf.jobAnalysis.editBanner(body.key, body.value), null, createAxiosTokenConfig(token))
    },
    // onError: (error) => {
    //   console.log('error', error)
    // },
  })
}

// Conversation AI - Caf Request Only
export const useCafRequestAnalysisComponentListV5 = (threadId: number, enabled: boolean) => {
  const pathParams = { threadId }

  return useQuery<CAFOverviewResponse, AxiosError>({
    queryKey: cafRequestAnalysisComponentListQueryApiV5.getQueryKey({ pathParams }),
    queryFn: cafRequestAnalysisComponentListQueryApiV5.getQueryFn({ pathParams }),

    // onError: (e) => handleAxiosError(e),
    enabled: !!threadId && enabled,
    staleTime: 60000,
    retry: false,
  })
}

export const useCafRequestHighlightListV5 = <T>({
  threadId,
  pinned = false,
  recommended = false,
  show_details = false,
}) => {
  const token = getJwtToken()
  return useQuery<T>({
    queryKey: [apis.caf.highlight.list(threadId), { pinned, recommended, show_details }, 'highlight'],
    queryFn: async () => {
      const { data } = await axios.get(apis.caf_request.highlight.list(threadId), {
        ...createAxiosTokenConfig(token),
        params: { pinned, recommended, show_details },
      })
      console.log('QUERY RESPONSE :: ', data)
      return data
    },
    enabled: !!threadId,
    retry: false,
    // onError: (err) => {
    //   console.log({ err })
    // },
  })
}
